import React, { useMemo, useState } from 'react';
import Modal from '../../components/ui/Modal';
import { Button, Input } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import UserPickerDropdown from '../../components/Settings/UserPickerDropdown';
import { api } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { AGENCY_ROLE_OPTIONS } from '../../lib/roleOptions';
import {
  getActiveDirectoryUsers,
  invalidateUsersDirectory,
  useUsersDirectory,
} from '../../lib/usersDirectory';

const ROLE_HINTS = {
  admin: 'Full access including settings.',
  manager: 'Can manage leads and view reports.',
  closer: 'Can close and update leads.',
  setter: 'Can log calls and create leads.',
  associate: 'Can work assigned leads with co-worker support.',
};

const MD_ROLES = new Set(['manager', 'admin', 'super_admin']);

export default function InviteUserModal({ isOpen, onClose, onInvite, agencyId }) {
  const { addToast } = useToast();
  const { users: directoryUsers } = useUsersDirectory();
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    role: 'setter',
    is_marketing_director: false,
    trainer_id: null,
    marketing_director_id: null,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const mdAllowlist = useMemo(
    () => getActiveDirectoryUsers(directoryUsers).filter((u) => MD_ROLES.has(u.role)),
    [directoryUsers],
  );

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    return e;
  };

  const resetForm = () => {
    setForm({
      full_name: '',
      email: '',
      role: 'setter',
      is_marketing_director: false,
      trainer_id: null,
      marketing_director_id: null,
    });
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    if (!agencyId) {
      addToast({ type: 'error', title: 'Cannot invite', message: 'No agency is selected for your account.' });
      return;
    }
    setLoading(true);
    const invitedEmail = form.email.trim();
    try {
      const newUser = await api.users.invite({
        email: invitedEmail,
        full_name: form.full_name.trim(),
        role: form.role,
        agency_id: agencyId,
      });

      const profileExtras = {
        trainer_id: form.trainer_id || null,
        marketing_director_id: form.marketing_director_id || null,
        is_marketing_director: !!form.is_marketing_director,
      };
      const hasExtras =
        profileExtras.trainer_id ||
        profileExtras.marketing_director_id ||
        profileExtras.is_marketing_director;

      let savedUser = newUser;
      if (hasExtras && newUser?.id) {
        savedUser = await api.users.updateProfile(newUser.id, profileExtras);
      }

      invalidateUsersDirectory();
      onInvite(savedUser);
      resetForm();
      addToast({
        type: 'success',
        title: 'Invitation sent',
        message: `We emailed ${invitedEmail} with a link to join.`,
      });
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Invitation failed',
        message: err?.message || 'Could not send the invitation. Try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Invite Team Member" className="max-w-md">
      <p className="text-sm text-gray-500 mb-5">
        We will send a secure invitation link to their work email. They will appear as inactive until they complete signup.
      </p>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="relative">
          <Input
            label="Full Name"
            value={form.full_name}
            onChange={(e) => { setForm((f) => ({ ...f, full_name: e.target.value })); setErrors((er) => ({ ...er, full_name: '' })); }}
            placeholder="Jane Smith"
            error={errors.full_name}
          />
        </div>
        <div className="relative">
          <Input
            label="Work Email"
            type="email"
            value={form.email}
            onChange={(e) => { setForm((f) => ({ ...f, email: e.target.value })); setErrors((er) => ({ ...er, email: '' })); }}
            placeholder="jane@agency.com"
            error={errors.email}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
          <select
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
            className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {AGENCY_ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-400">
            {ROLE_HINTS[form.role] || ''}
          </p>
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_marketing_director}
            onChange={(e) => setForm((f) => ({ ...f, is_marketing_director: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-sm font-medium text-gray-700">Is Marketing Director</span>
        </label>

        <UserPickerDropdown
          label="Assign Trainer (optional)"
          source="directory"
          value={form.trainer_id}
          onChange={(id) => setForm((f) => ({ ...f, trainer_id: id }))}
          placeholder="Select trainer…"
        />
        <UserPickerDropdown
          label="Assign Marketing Director (optional)"
          source="directory"
          users={mdAllowlist}
          value={form.marketing_director_id}
          onChange={(id) => setForm((f) => ({ ...f, marketing_director_id: id }))}
          placeholder="Select marketing director…"
        />

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button type="button" variant="ghost" onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button type="submit" disabled={loading}>
            {loading ? <><Spinner className="mr-2" />Sending…</> : 'Send Invitation'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
